import React, { useState, useEffect } from 'react'
import { IDepartment, IFilter } from './index'
import { Text } from '../../elements/Text'
import { containerMaxWidth, mq } from 'marketing-site/src/library/utils'
import { JobCategory } from './jobCategory'
import { Search } from '../../elements/Search'
import { DropDown } from '../../elements/DropDown'
import { CheckBox } from '../../elements/CheckBox'
import TeamCTA from './TeamCTA'
import Masonry from 'react-masonry-component'

type setFilter = (filters: IFilter[]) => void

const toggleFilter = (
  filter: IFilter,
  checked: boolean,
  filters: IFilter[],
  setFilters: setFilter,
) => {
  setFilters(
    filters.map((f) => {
      if (f.value == filter.value) f.checked = checked
      return f
    }),
  )
}

interface IJobsProps {
  title: string
  noResults: string
  officeData: IDepartment[]
  officeId: number | undefined
  showTeamPageCTA: boolean
}

export function Jobs({
  officeData,
  title,
  noResults,
  officeId,
  showTeamPageCTA,
}: IJobsProps): React.ReactElement {
  const [search, setSearch] = useState<string>('')
  const [organizationFilters, setOrganizationFilters] = useState<IFilter[] | undefined>()
  const [locationFilters, setLocationFilters] = useState<IFilter[] | undefined>()
  let filteredOfficeDataHasJobs = false

  const columnGap = 6.875
  const columnGapInPx = columnGap * 16

  const filteredOfficeData = officeData.map((department) => {
    const organizationFilter = organizationFilters?.find(
      (filter) => filter.value === department.name,
    )
    const jobs = (organizationFilter?.checked ? department.jobs : []).filter((job) => {
      const name = job.location.name.split(',')[0].trim()
      const locationFilter = locationFilters?.find((filter) => filter.value === name)
      const jobMatchSearch = !search || job.title.toLowerCase().match(search.toLowerCase())
      return locationFilter?.checked && jobMatchSearch
    })
    if (jobs.length) filteredOfficeDataHasJobs = true

    return {
      ...department,
      jobs,
    }
  })

  useEffect(() => {
    setOrganizationFilters(
      officeData.map((item: IDepartment) => ({
        value: item.name,
        label: `${item.name} (${item.jobs.length})`,
        checked: true,
      })),
    )

    const allLocations = officeData
      .reduce(
        (locations: string[], item: IDepartment) => [
          ...locations,
          ...item.jobs.map((job) => job.location.name.split(',')[0].trim()),
        ],
        [],
      )
      .sort((a: string, b: string) => {
        const textA = a.toUpperCase()
        const textB = b.toUpperCase()
        return textA < textB ? -1 : textA > textB ? 1 : 0
      })

    setLocationFilters(
      [...new Set(allLocations)].map((location: string) => ({
        value: location,
        label: `${location} (${allLocations.filter((l) => l === location).length})`,
        checked: true,
      })),
    )
  }, [officeData])

  return (
    <div className="greenhouse-listings" id="jobs">
      <div className="greenhouse-listings__header">
        <div className="greenhouse-listings__title">
          <Text brand="default" size="heading-3">
            {title}
          </Text>
        </div>

        <div className="greenhouse-listings__filters">
          <span className="greenhouse-listings__filters-label">
            <Text brand="default" size="caption">
              {`Filter:`}
            </Text>
          </span>
          <div className="greenhouse-listings__filters-container">
            {locationFilters && !officeId && (
              <DropDown
                label="Location"
                openerClassNames="dropdown__opener--no-right-border dropdown__opener--no-right-radius"
              >
                <ul role="listbox">
                  {locationFilters.map((filter) => (
                    <li
                      // eslint-disable-next-line
                      role="option"
                      className="greenhouse-listings__filter-option"
                      key={filter.label}
                    >
                      <CheckBox
                        label={filter.label}
                        checked={filter.checked}
                        onChange={(checked: boolean) =>
                          toggleFilter(filter, checked, locationFilters, setLocationFilters)
                        }
                      />
                    </li>
                  ))}
                </ul>
              </DropDown>
            )}
            {organizationFilters && (
              <DropDown
                label="Organization"
                openerClassNames={officeId ? '' : 'dropdown__opener--no-left-radius'}
              >
                <ul role="listbox">
                  {organizationFilters.map((filter) => (
                    <li
                      // eslint-disable-next-line
                      role="option"
                      className="greenhouse-listings__filter-option"
                      key={filter.label}
                    >
                      <CheckBox
                        label={filter.label}
                        checked={filter.checked}
                        onChange={(checked: boolean) =>
                          toggleFilter(filter, checked, organizationFilters, setOrganizationFilters)
                        }
                      />
                    </li>
                  ))}
                </ul>
              </DropDown>
            )}
            <div className="greenhouse-listings__filters-search">
              <Search value={search} handleSearch={setSearch} />
            </div>
          </div>
        </div>
      </div>

      <Masonry
        className="greenhouse-listings__department-listings"
        options={{
          gutter: columnGapInPx,
        }}
      >
        {filteredOfficeDataHasJobs ? (
          filteredOfficeData.map((department) => {
            if (!department.jobs.length) return null

            return (
              <JobCategory
                title={department.name}
                jobs={department.jobs}
                key={department.id}
                cta={showTeamPageCTA ? <TeamCTA department={department} /> : null}
              />
            )
          })
        ) : (
          <div className="greenhouse-listings__no-results">
            <Text brand="default" size="heading-3">
              {noResults}
            </Text>
          </div>
        )}
      </Masonry>

      <style jsx>
        {`
          .greenhouse-listings {
            width: calc(100% / 14 * 12);
            max-width: ${containerMaxWidth};
            margin: 0 auto;
            display: grid;
            grid-gap: 2.5rem;

            &__filters {
              display: flex;
              flex-wrap: wrap;
              margin-top: 1.125rem;
              align-items: center;
              position: relative;
            }

            &__filters-container {
              display: flex;
              flex-grow: 1;
            }

            &__filters-search {
              max-width: 100%;
              margin-left: 1rem;
              flex-grow: 1;

              :global(.search__opener) {
                width: 100%;
                text-align: center;
              }
            }

            &__filters-label {
              padding-right: 1rem;
            }

            &__no-results {
              grid-column: 1/-1;
              padding: 5rem 0;
              text-align: center;
            }

            &__filter-option + &__filter-option {
              margin-top: 0.625rem;
            }

            :global(.greenhouse-listings__department-listings) {
              display: flex;
              flex-wrap: wrap;
              column-gap: ${columnGap}rem;
            }
          }

          @media (max-width: 29.5em) {
            .greenhouse-listings {
              :global(.search__field-input-wrap) {
                bottom: -165%;
              }

              &__filters-label {
                padding-right: 0.25rem;
              }

              &__filters-container {
                :global(.dropdown__opener) {
                  padding: 0.375rem 1.5rem 0.375rem 0.625rem;
                }

                :global(.dropdown__opener-chevron) {
                  right: 0.5rem;
                }
              }

              &__filters-search {
                margin-left: 0.5rem;
              }
            }
          }

          @media (max-width: 31.25em) {
            .greenhouse-listings {
              :global(.dropdown__wrap:first-of-type .dropdown__content) {
                left: 110%;

                &::before {
                  left: 23%;
                }
              }

              :global(.dropdown__wrap:nth-child(2) .dropdown__content) {
                left: 25%;

                &::before {
                  left: 60%;
                }
              }
            }
          }

          @media (${mq.tablet}) {
            .greenhouse-listings {
              :global(.heading span) {
                font-size: 2.75rem;
              }
            }
          }

          @media (${mq.laptop}) {
            .greenhouse-listings {
              grid-gap: 4rem;

              &__header {
                display: grid;
                grid-template-columns: [left] 1fr [right] 1fr;
                grid-column-gap: 2.5rem;
                grid-row-gap: 2.5rem;
                align-items: center;
              }

              &__filters {
                margin-top: 0;
                flex-wrap: nowrap;
              }

              &__filters-search {
                margin-top: 0;
                margin-left: 2rem;
                width: 15.625rem;
                height: 2.3125rem;
                position: relative;
              }
            }
          }

          @media (${mq.desktop}) {
            .greenhouse-listings {
              margin: 0 auto;
            }
          }
        `}
      </style>
    </div>
  )
}
